import { useMemo } from "react";
import BottleForm from "../../components/forms/bottleForm";
import BottleTable from "../../components/tables/bottleTable";
import LoadingState from "../../components/ui/LoadingState";
import EmptyState from "../../components/ui/EmptyState";
import SectionSummaryBar from "../../components/ui/SectionSummaryBar";
import { useFirestore } from "../../hooks/useFirestore";
import { useChild } from "../../contexts/ChildContext";
import { exportToCSV, bottleCSVColumns } from "../../utils/exportUtils";
import { exportSectionPDF } from "../../utils/pdfUtils";
import { parseItemDate, formatTimeAgo } from "../../utils/dateUtils";

const BottleFeeds = () => {
  const { selectedChild } = useChild();
  const { items: bottles, addItem, updateItem, deleteItem, loading, error } =
    useFirestore("bottleFeeds", selectedChild?.id);

  // Most recent entry — used to pre-fill the add form
  const lastEntry = useMemo(() => {
    if (!bottles.length) return null;
    return [...bottles].sort((a, b) => {
      const da = parseItemDate(a), db_ = parseItemDate(b);
      return (db_ ?? 0) - (da ?? 0);
    })[0];
  }, [bottles]);

  // Today at a glance + next-feed prediction
  const todayStats = useMemo(() => {
    const todayStr = new Date().toDateString();
    const sorted = [...bottles]
      .map(b => ({ item: b, date: parseItemDate(b) }))
      .filter(x => x.date)
      .sort((a, b) => b.date - a.date);

    const todayFeeds = sorted.filter(x => x.date.toDateString() === todayStr);
    const count = todayFeeds.length;
    const oz = todayFeeds.reduce((s, x) => s + parseFloat(x.item.amount || 0), 0);
    const lastFeedDate = sorted[0]?.date ?? null;

    // Average interval from last 6 feeds for next-feed prediction
    let nextDueMin = null;
    if (sorted.length >= 2) {
      const times = sorted.slice(0, 7).map(x => x.date.getTime());
      const gaps = [];
      for (let i = 0; i < times.length - 1; i++) {
        const gap = (times[i] - times[i + 1]) / 60000;
        if (gap > 0 && gap < 480) gaps.push(gap); // ignore > 8h gaps
      }
      if (gaps.length > 0) {
        const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
        nextDueMin = Math.round((times[0] + avgGap * 60000 - Date.now()) / 60000);
      }
    }

    return { count, oz, lastFeedDate, nextDueMin };
  }, [bottles]);

  const summaryStats = useMemo(() => {
    if (!bottles.length) return null;
    const { count, oz, lastFeedDate, nextDueMin } = todayStats;
    const stats = [
      { value: String(count), label: count === 1 ? 'feed today' : 'feeds today' },
      { value: `${oz.toFixed(1)} oz`, label: 'today' },
      { value: formatTimeAgo(lastFeedDate) ?? '—', label: 'last fed' },
    ];
    if (nextDueMin !== null) {
      const isOverdue = nextDueMin <= 0;
      const isWarning = nextDueMin > 0 && nextDueMin <= 20;
      stats.push({
        value: isOverdue ? 'Due now' : `~${nextDueMin}m`,
        label: 'next feed',
        accent: isOverdue ? 'danger' : isWarning ? 'warning' : undefined,
      });
    }
    return stats;
  }, [bottles, todayStats]);

  const handleAddBottle = async (bottle) => {
    try {
      await addItem({ ...bottle, childId: selectedChild?.id });
    } catch (err) {
      console.error("Error adding bottle feed:", err);
    }
  };

  const handleExport = () =>
    exportToCSV(bottles, `bottle-feeds-${selectedChild?.name || "export"}.csv`, bottleCSVColumns);

  const handleExportPDF = () =>
    exportSectionPDF(bottles, `bottle-feeds-${selectedChild?.name || "export"}.pdf`, bottleCSVColumns, "Bottle Feeds", selectedChild?.name || "");

  return (
    <div className="card h-100" id="bottle-feeds">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">🍼 Bottle Feeds</h5>
          <div className="d-flex gap-1">
            <button className="btn btn-sm btn-outline-secondary" onClick={handleExport}>⬇ CSV</button>
            <button className="btn btn-sm btn-outline-primary" onClick={handleExportPDF}>⬇ PDF</button>
          </div>
        </div>

        {!loading && summaryStats && <SectionSummaryBar stats={summaryStats} />}

        {error && <div className="alert alert-danger">{error}</div>}
        <BottleForm onAdd={handleAddBottle} lastEntry={lastEntry} />
        {loading ? (
          <LoadingState message="Loading bottle feeds..." />
        ) : bottles.length > 0 ? (
          <BottleTable data={bottles} onUpdate={updateItem} onDelete={deleteItem} />
        ) : (
          <EmptyState
            icon="🍼"
            heading="No feeds logged yet"
            message="Every feed counts — add your first bottle to start tracking."
          />
        )}
      </div>
    </div>
  );
};

export default BottleFeeds;
